import { singleton } from "tsyringe";
import { UserDataLayerAdapter } from "../../shared/UserDataLayerAdapter";
import { UserIdentifiersDto, UserIdentifiersStorage } from "./UserIdentifiersDto";
import { makeJsonRequest } from "../../utils/JsonRequests";
import { Logger } from "../../utils/Logger";
import { HttpMethods } from "../../utils/HttpMethods";
import { getCookie } from "typescript-cookie";
import { CookiesNames } from "../../gtm/Cookies";

declare const UIdsUrl: string;

/**
 * Client used to interact with Caspian's endpoint used to collect user identifier pairs.
 */
@singleton()
export default class CaspianUserIdentifiersApiClient {
  // For some clients, a new lotameId is generated on each page view.
  // This constraint is used to limit the number of lotame IDs pairs that are sent to Caspian.
  // Anyway, we can't do any meaningful targeting on users that doesn't have a stable LotameId.
  maxLotameIds = 2;
  private sentLotameIds: string[] = [];
  private rcUieLocalStorageKey = "uie-src-ids";

  public async sendUserIdentifiers(userAdapter: UserDataLayerAdapter, lotameId: string | undefined): Promise<void> {
    const adobeMarketingCloudOrgId = "55E654E45894AF350A495CFE@AdobeOrg";

    let adobeMarketingCloudVisitorId: string | undefined;

    try {
      const visitor = Visitor.getInstance(adobeMarketingCloudOrgId);
      adobeMarketingCloudVisitorId = visitor.getMarketingCloudVisitorID();
    } catch (e) {
      Logger.error(`Call to Adobe failed. ${e}`);
    }

    await this.sanitizeAndSendIdentifiers(adobeMarketingCloudVisitorId, lotameId, userAdapter);
  }

  private async sanitizeAndSendIdentifiers(
    adobeMarketingCloudVisitorId: string | undefined,
    lotameId: string | undefined,
    userAdapter: UserDataLayerAdapter,
  ): Promise<void> {
    const userIdentifiers: UserIdentifiersDto = {
      adobeMarketingCloudVisitorId: sanitize(adobeMarketingCloudVisitorId),
      lotameId: sanitize(lotameId),
      ppid: getCookie(CookiesNames.rcPPId),
      rcId: undefined,
      rcVisitor: undefined,
    };

    if (!userIdentifiers.adobeMarketingCloudVisitorId) {
      Logger.warn("visitor.getMarketingCloudVisitorID() returned an empty value");
      return;
    }

    const sanitizedRcId = sanitize(userAdapter.rcId);
    const sanitizedVisitorId = sanitize(userAdapter.rc_visitor_id);

    if (sanitizedRcId) {
      userIdentifiers.rcId = sanitizedRcId;
    } else if (sanitizedVisitorId) {
      userIdentifiers.rcVisitor = sanitizedVisitorId;
    } else {
      Logger.warn("Can not send user identifiers: no rcId or rc_visitor_id available.");
      return;
    }

    if (!this.hasUserIdentifiersChanged(userIdentifiers)) {
      Logger.verbose("User identifiers unchanged."); // Warning: this message is used in tests.
      return;
    }
    if (userIdentifiers.lotameId && !this.sentLotameIds.includes(userIdentifiers.lotameId)) {
      this.sentLotameIds.push(userIdentifiers.lotameId);
    }
    if (this.sentLotameIds.length <= this.maxLotameIds) {
      Logger.verbose("Sending user identifiers to Caspian API.", userIdentifiers);
      let response: Response;
      try {
        response = await makeJsonRequest(UIdsUrl, HttpMethods.POST, "omit", userIdentifiers);
      } catch (e) {
        Logger.error(`Failed to make a Json Request to send user identifiers. Error: ${e}`);
        return;
      }
      if (!response.ok) {
        await Logger.requestError("Failed to send User identifiers", response);
        return;
      }
    } else {
      Logger.error(
        `Maximum number of lotame Ids for the session reached. Current count: ${this.sentLotameIds.length}`,
        {
          lotameIdCount: this.sentLotameIds.length,
        },
      );
    }
    const storage = userIdentifiers as UserIdentifiersStorage;
    storage.expireOn = Date.now() + 1000 * 60 * 60 * 24; // 1 Day in milliseconds
    const userIdentifiersString = JSON.stringify(userIdentifiers);
    localStorage.setItem(this.rcUieLocalStorageKey, userIdentifiersString);
    Logger.verbose("User identifiers set in storage."); // Used in tests!
  }

  private hasUserIdentifiersChanged(newUserIdentifiers: UserIdentifiersDto): boolean {
    const localStorageData = localStorage.getItem(this.rcUieLocalStorageKey);
    if (!localStorageData) {
      return true;
    }
    let serializedIdentifiers: UserIdentifiersStorage | undefined;
    try {
      serializedIdentifiers = JSON.parse(localStorageData) as UserIdentifiersStorage;
    } catch (ex) {
      Logger.error(`Could not deserialize user identifiers. Error: ${ex}`);
      return true;
    }

    if (!serializedIdentifiers) {
      return true;
    }

    if (!serializedIdentifiers.expireOn || serializedIdentifiers.expireOn < Date.now()) {
      return true;
    }

    return (
      newUserIdentifiers.adobeMarketingCloudVisitorId !== serializedIdentifiers.adobeMarketingCloudVisitorId ||
      newUserIdentifiers.lotameId !== serializedIdentifiers.lotameId ||
      newUserIdentifiers.ppid !== serializedIdentifiers.ppid ||
      newUserIdentifiers.rcId !== serializedIdentifiers.rcId ||
      newUserIdentifiers.rcVisitor !== serializedIdentifiers.rcVisitor
    );
  }
}

function sanitize(identifier: string | undefined): string | undefined {
  if (!identifier) {
    return undefined;
  }
  if (identifier.toLowerCase() === "null" || identifier.toLowerCase() === "undefined") {
    return undefined;
  }
  if (identifier === "00000000-0000-0000-0000-000000000000" || identifier === "00000000000000000000000000000000") {
    return undefined;
  }
  return identifier;
}
